import {
  deleteJsonRequest,
  factoryService,
  getFileRequest,
  getJsonRequest,
  postFormRequest,
  postJsonRequest
} from '@chilecompra/react-kit/requests'

import { SANCTIONS_ROWS_PER_PAGE } from '../config/settings/constant'
import {
  REACT_APP_API_BASE_URL,
  REACT_APP_CONTRACTUAL_BEHAVIOR_BY_REVIEWS_BASE_URL,
  REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_V2,
  REACT_APP_FEATURE_FLAG_PO_BIDDING,
  REACT_APP_REVIEWED_CONTRACTS_BASE_URL
} from '../config/settings/environment'
import { normalizeDniSlashes } from '../modules/utils/normalizeForeignDni'

/**
 * Gets base contractual behavior's summary for a given seller.
 */
export const getContractualBehaviorSummary = factoryService(({ dni, isForeign }) => {
  if (
    REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_V2 === true ||
    REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_V2 === 'true'
  ) {
    return getJsonRequest(
      `${REACT_APP_API_BASE_URL}/v2/proveedor/sancion/comportamiento/${normalizeDniSlashes(dni)}/${isForeign}`
    )
  }
  return getJsonRequest(
    `${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/comportamiento/${normalizeDniSlashes(dni)}/${isForeign}`
  )
})

/**
 * Gets contractual behavior summary by reviews for a given seller.
 */
export const getContractualBehaviorSummaryByReviews = factoryService(({ dni }) => {
  return getJsonRequest(
    `${REACT_APP_CONTRACTUAL_BEHAVIOR_BY_REVIEWS_BASE_URL}/v1/comportamiento-contractual/promedio/${normalizeDniSlashes(dni)}`
  )
})

/**
 * Gets sanctions for a given seller by process type. When is 'all' it means that it returns
 * data for all types of processes.
 */
export const getSanctionsByProcessType = factoryService(({ dni, isForeign, processType }) => {
  if (
    REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_V2 === true ||
    REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_V2 === 'true'
  ) {
    if (REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true') {
      return getJsonRequest(
        `${REACT_APP_API_BASE_URL}/v3/proveedor/sancion/${normalizeDniSlashes(dni)}/${isForeign}/${processType}`
      )
    } else {
      return getJsonRequest(
        `${REACT_APP_API_BASE_URL}/v2/proveedor/sancion/${normalizeDniSlashes(dni)}/${isForeign}/${processType}`
      )
    }
  } else {
    if (REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true') {
      return getJsonRequest(
        `${REACT_APP_API_BASE_URL}/v3/proveedor/sancion/${normalizeDniSlashes(dni)}/${isForeign}/${processType}`
      )
    } else {
      return getJsonRequest(
        `${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/${normalizeDniSlashes(dni)}/${isForeign}/${processType}`
      )
    }
  }
})

/**
 * Gets the login user information to enter sanction.
 */
export const getUserInfo = factoryService(() =>
  getJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/comprador/`)
)

/**
 * Gets the purchase order.
 */
export const getPOBiddingInfo = factoryService(({ externalCode, dni, processType }) => {
  return getJsonRequest(
    `${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/${processType}/${normalizeDniSlashes(dni)}/${externalCode}`
  )
})

/**
 * Gets the sanctions types.
 */
export const getSanctionsTypes = factoryService(() => {
  return getJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/tipossancion`)
})

/**
 * Gets the attachments types.
 */
export const getAttachmentsTypes = factoryService(() => {
  return getJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/tiposadjunto`)
})

/**
 * To register a sanction to a given seller and also create and relate the attachment file.
 */
export const registerSanction = factoryService(
  ({
    attachmentType,
    backupDate,
    dni,
    docFolio,
    fileName,
    id,
    isForeign,
    externalCode,
    reason,
    resolutionNumber,
    sanctionType,
    processType
  }) => {
    const body =
      REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true'
        ? {
            motivo: reason,
            tiposancion: sanctionType,
            tipoproceso: processType,
            codigoexterno: externalCode,
            nroproceso: id,
            rutproveedor: normalizeDniSlashes(dni),
            extranjero: isForeign,
            documentos: [
              {
                docfolio: docFolio,
                tipoadjunto: attachmentType,
                nrodocumento: resolutionNumber,
                nombrearchivo: fileName,
                fechadocumento: backupDate
              }
            ]
          }
        : {
            motivo: reason,
            tiposancion: sanctionType,
            ordencompra: externalCode,
            rutproveedor: normalizeDniSlashes(dni),
            extranjero: isForeign,
            documentos: [
              {
                docfolio: docFolio,
                tipoadjunto: attachmentType,
                nrodocumento: resolutionNumber,
                nombrearchivo: fileName,
                fechadocumento: backupDate
              }
            ]
          }

    const version =
      REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true' ? 'v3' : 'v1'
    return postJsonRequest(`${REACT_APP_API_BASE_URL}/${version}/proveedor/sancion/`, { body })
  }
)

/**
 * Gets the sanction details.
 */
export const getSanctionDetails = factoryService(({ id }) => {
  return getJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/${id}`)
})

/**
 * To delete a given sanction.
 */
export const removeSanction = factoryService(({ id, justification }) => {
  const body = {
    justificacionAnulacion: justification
  }
  return deleteJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/${id}`, { body })
})

/**
 * Gets historical sanctions list.
 */
export const getHistoricalSanctionsList = factoryService(
  ({ dni, isForeign, qs: { itemsPerPage = SANCTIONS_ROWS_PER_PAGE, keyword = '', page = 1 } }) => {
    const params = new URLSearchParams({
      ...(keyword && {
        keyword
      }),
      page,
      size: itemsPerPage
    }).toString()

    return getJsonRequest(
      `${REACT_APP_API_BASE_URL}/v3/proveedor/sancion/historico/${normalizeDniSlashes(dni)}/${isForeign}/ALL?${params}`
    )
  }
)

/**
 * Gets sanction void info.
 */
export const getSanctionVoidInfo = factoryService(({ sanctionId }) => {
  return getJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/sancion/${sanctionId}/anulada`)
})

/**
 * Gets all the reviewed contracts for a given seller.
 */
export const getReviewedContracts = factoryService(({ dni }) => {
  // FIXME: Update base url when service is correctly hosted. This is a temporary location.
  // Check in local when yo make the call just to be sure is working
  return getJsonRequest(
    `${REACT_APP_REVIEWED_CONTRACTS_BASE_URL}/v1/comportamiento-contractual/lista-contratos/${normalizeDniSlashes(dni)}`
  )
})

/**
 * Execute the revoke request for a sanction.
 * The backend now expects multipart/form-data: a "retiro" text part (JSON with id + justificacion)
 * and a "file" part with the PDF that backs the administrative act. The service uploads the file
 * directly, so no separate document-manager upload is needed anymore.
 */
export const postRebokeRequest = factoryService(({ justification, id, file }) => {
  const body = {
    retiro: JSON.stringify({ id, justificacion: justification }),
    file
  }
  return postFormRequest(`${REACT_APP_API_BASE_URL}/v3/proveedor/sancion/solicitud-retiro`, { body })
})

/**
 * Downloads a sanction attachment uploaded via the document-manager WS (e.g. the administrative act
 * that backs a withdrawal). The file is resolved by its atcCode.
 */
export const downloadRevokeFile = factoryService(({ atcCode }) => {
  return getFileRequest(`${REACT_APP_API_BASE_URL}/v3/proveedor/sancion/descargar-archivo/${atcCode}`)
})
